import { Button, Image, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import axios, { type AxiosResponse } from 'axios';
import type { UploadRequestOption } from 'rc-upload/lib/interface';
import React from 'react';

import type { SubscriptionPlan } from '@/types/database/subscriptionPlan';
import type { Uploads } from '@/types/database/uploads';
import type UserGroupRoute from '@/types/database/userGroupRoute';

import AddAndEditPlanModalWithButton from './AddOrEditModalWithButton';
import styles from './SubscriptionPlanSetting/index.module.scss';

export const getSubscriptionPlansColumns = (args: {
  checkDetail: (record: SubscriptionPlan) => void;
  updatePlanAvailability: (record: SubscriptionPlan) => void;
  updateSubscriptionPlans: () => void;
  updateUploads: () => void;
  uploads: Uploads[];
  userGroupRoutes: UserGroupRoute[];
}) => {
  const {
    checkDetail,
    updatePlanAvailability,
    updateSubscriptionPlans,
    updateUploads,
    uploads,
    userGroupRoutes,
  } = args;

  const columns: ColumnsType<SubscriptionPlan & { serviceName: string }> = [
    {
      title: '計費方案名稱',
      dataIndex: 'planName',
    },
    {
      title: '狀態',
      dataIndex: 'isPlanActive',
      render: (value) => (value ? '已啟用' : '已停用'),
    },
    {
      title: '服務',
      dataIndex: 'serviceName',
    },
    {
      title: '方案用戶',
      dataIndex: 'communityName',
    },
    {
      title: '效期',
      dataIndex: 'expiration',
    },
    {
      title: '金額(台灣Pay)',
      dataIndex: 'creditCardPrice',
      render: (value) => (value ? `${value}元` : ''),
    },
    {
      title: '金額(點數)',
      dataIndex: 'points',
      render: (_value, record) =>
        record.points.quantity ? `${record.points.quantity}點` : '',
    },
    {
      title: '方案介紹',
      dataIndex: 'description',
    },
    {
      title: '方案規則',
      dataIndex: 'rules',
    },
    {
      title: '操作',
      dataIndex: 'actions',
      fixed: 'right',
      render: (_value, record) => {
        return (
          <Space>
            <Button type="primary" onClick={() => checkDetail(record)}>
              查看
            </Button>
            <AddAndEditPlanModalWithButton
              type="edit"
              updateSubscriptionPlans={updateSubscriptionPlans}
              updateUploads={updateUploads}
              record={record}
              uploads={uploads}
              userGroupRoutes={userGroupRoutes}
            />
            <Button
              type="primary"
              style={{
                backgroundColor: record.isPlanActive ? '#D23C26' : '#32C391',
                borderWidth: 0,
              }}
              onClick={() => updatePlanAvailability(record)}
            >
              {record.isPlanActive ? '停用' : '啟用'}
            </Button>
          </Space>
        );
      },
    },
  ];

  return columns;
};

const checkIsIncludedPlanName = (
  planNames: { label: string; value: string }[],
  planName: string
) => {
  const isIncludedPlanName = planNames.some((planNameObject) => {
    return planNameObject.value === planName;
  });
  return isIncludedPlanName;
};

export const getAllPlanOptions = (subscriptionPlans: SubscriptionPlan[]) => {
  const planOptions = subscriptionPlans.reduce(
    (planNames: { label: string; value: string }[], subscriptionPlan) => {
      if (
        checkIsIncludedPlanName(planNames, subscriptionPlan.planName) === false
      ) {
        planNames.push({
          label: subscriptionPlan.planName,
          value: subscriptionPlan.planName,
        });
      }

      return planNames;
    },
    []
  );
  return planOptions;
};

export const expirationOptions = [
  {
    label: '1個月',
    value: '1個月',
  },
  {
    label: '3個月',
    value: '3個月',
  },
  {
    label: '6個月',
    value: '6個月',
  },
  {
    label: '1年',
    value: '1年',
  },
  {
    label: '2年',
    value: '2年',
  },
];

export const uploadFiles = (
  file: string | Blob
): Promise<AxiosResponse<Uploads>> => {
  const formData = new FormData();
  formData.append('files', file);
  const res = axios.post(
    `${window.location.pathname}newUploads?type=subscriptionPlan`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        authorization: localStorage.getItem('feathers-jwt'),
      },
    }
  );
  return res;
};

export const customRequest = async (options: UploadRequestOption<Uploads>) => {
  const { file, onSuccess } = options;
  const res = await uploadFiles(file);
  onSuccess && onSuccess(Array.isArray(res.data) ? res.data[0] : res.data);
};

export const getFileListFromEvent = ({
  fileList,
}: {
  fileList: File & { response: Uploads }[];
}) => {
  if (fileList.length !== 0 && fileList[0].response) {
    const url = fileList[0].response.showPath;
    const previewableFileList = [{ ...fileList[0], url }];
    return previewableFileList;
  }

  return fileList;
};

export const checkDetailModalBodyStyles: React.CSSProperties = {
  maxHeight: '80vh',
  overflowY: 'scroll',
};

export const addAndEditPlanModalBodyStyles: React.CSSProperties = {
  maxHeight: '90vh',
  overflowY: 'scroll',
};

const fields = [
  {
    title: '計費方案名稱',
    content: 'planName',
  },
  {
    title: '狀態',
    content: 'isPlanActive',
  },
  {
    title: '服務',
    content: 'serviceName',
  },
  {
    title: '方案用戶',
    content: 'communityName',
  },
  {
    title: '效期',
    content: 'expiration',
  },
  {
    title: '金額(台灣Pay)',
    content: 'creditCardPrice',
  },
  {
    title: '金額(點數)',
    content: 'points',
  },
  {
    title: '方案介紹',
    content: 'description',
  },
  {
    title: '方案規則',
    content: 'rules',
  },
  {
    title: '圖片',
    content: 'file',
  },
];

export const renderPlanDetail = (
  subscriptionPlanDetailRecord: SubscriptionPlan | undefined
) => {
  const detailFields = fields.map(
    ({ title, content }: { title: string; content: string }) => {
      let specificElement = (
        <p>
          {subscriptionPlanDetailRecord &&
            subscriptionPlanDetailRecord[content]}
        </p>
      );

      if (content === 'isPlanActive') {
        specificElement = (
          <p>
            {subscriptionPlanDetailRecord &&
            subscriptionPlanDetailRecord[content]
              ? '已啟用'
              : '已停用'}
          </p>
        );
      }

      if (content === 'creditCardPrice') {
        specificElement = (
          <p>
            {subscriptionPlanDetailRecord &&
            subscriptionPlanDetailRecord[content]
              ? `${subscriptionPlanDetailRecord[content]}元`
              : ''}
          </p>
        );
      }

      if (content === 'points') {
        specificElement = (
          <p>
            {subscriptionPlanDetailRecord &&
            subscriptionPlanDetailRecord[content].quantity
              ? `${subscriptionPlanDetailRecord[content].quantity}點`
              : ''}
          </p>
        );
      }

      if (content === 'file') {
        const src =
          subscriptionPlanDetailRecord && subscriptionPlanDetailRecord[content];
        specificElement = <Image src={src ?? ''} />;
      }

      return (
        <div key={title}>
          <h5 className={styles.fieldTitle}>{`${title}:`}</h5>
          {specificElement}
        </div>
      );
    }
  );

  return detailFields;
};

const checkIsIncludedServiceName = (
  serviceOptions: { label: string; value: string }[],
  serviceName: string | undefined
) => {
  if (serviceName) {
    const isIncludedService = serviceOptions.some(
      (service) => service.value === serviceName
    );
    return isIncludedService;
  }
  return true;
};

export const getServiceOptionsForFilter = (
  subscriptionPlans: SubscriptionPlan[],
  userGroupRoutes: UserGroupRoute[]
) => {
  const options = subscriptionPlans.reduce(
    (serviceOptions: { label: string; value: string }[], plan) => {
      const foundService = userGroupRoutes.find(
        (userGroupRoute) => userGroupRoute._id === plan.serviceId
      );
      const isIncludedService = checkIsIncludedServiceName(
        serviceOptions,
        foundService?.name
      );
      if (foundService && isIncludedService === false) {
        serviceOptions.push({
          label: foundService.name,
          value: foundService.name,
        });
      }
      return serviceOptions;
    },
    []
  );

  return options;
};

export const getServiceId = (
  userGroupRoutes: UserGroupRoute[],
  serviceName: string
) => {
  const serviceId = userGroupRoutes.find(
    (userGroupRoute) =>
      userGroupRoute.name === serviceName && userGroupRoute.isPointActive
  )?._id;
  return serviceId;
};

export const serviceActiveOptions = [
  {
    label: '開',
    value: true,
  },
  {
    label: '關',
    value: false,
  },
];

export const checkFileType = (
  file: UploadFile[] | Uploads | Record<string, never>
): file is Uploads => {
  return file instanceof Array === false && Object.keys(file).length > 0;
};

export const getFileData = (
  file:
    | UploadFile<Uploads>[]
    | Uploads
    | Record<string, never>
    | null
    | undefined
) => {
  if (file === null || file === undefined) {
    return null;
  }

  const isTypeofUploads = checkFileType(file);

  if (isTypeofUploads) {
    return file as Uploads;
  }

  if (
    isTypeofUploads === false &&
    (file as UploadFile<Uploads>[]).length !== 0
  ) {
    return file as UploadFile<Uploads>[];
  }

  return file;
};

export const checkPlanNameIsExist = (args: {
  subscriptionPlans: SubscriptionPlan[];
  planName: string;
  formType: 'add' | 'edit';
}) => {
  const { subscriptionPlans, planName, formType } = args;

  if (!planName) {
    return Promise.reject(new Error('請輸入計費方案名稱'));
  }
  if (formType === 'add') {
    const isExist = subscriptionPlans.some(
      (plan) => plan.planName === planName
    );
    return isExist
      ? Promise.reject(new Error('方案名稱已存在'))
      : Promise.resolve();
  }

  return Promise.resolve();
};
